import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { PAGE_SIZE_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import { PrettySelectComponent } from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { TransactionsService } from '../../../shared/services/transactions.service';
import {
  METADATA_TYPE_LABEL,
  SOURCE_SERVICE_LABEL,
  TRANSACTION_STATUS_META,
  Transaction,
  parseTransactionMetadata,
} from '../../../shared/types/transaction.types';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';

/**
 * Liste self-service des transactions du user courant. Endpoint
 * `/api/transactions/me` — paginé côté back depuis le patch v2
 * (enveloppe Nawam ApiCollection, 1-indexé). Pas de filtre RSQL/statut/source
 * côté owner : la collection est petite et la vue est purement consultative.
 */
interface TxRow extends Transaction {
  _metadataTypeLabel: string;
  _recipientLabel: string;
}

@Component({
  selector: 'app-transactions-list',
  imports: [CommonModule, FormsModule, RouterModule, PageHeaderComponent, DataStateComponent, PrettySelectComponent, TPipe, LocalizedDatePipe],
  templateUrl: './transactions-list.component.html',
})
export class TransactionsListComponent implements OnInit {
  private readonly txService = inject(TransactionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly txs = signal<TxRow[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(10);

  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly statusMeta = TRANSACTION_STATUS_META;
  readonly sourceLabel = SOURCE_SERVICE_LABEL;

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.txService
      .listMine({ page: this.page(), size: this.size() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const rows: TxRow[] = (res.data ?? []).map((t) => {
            const meta = parseTransactionMetadata(t.metadata);
            const typeLabel = meta?.type
              ? METADATA_TYPE_LABEL[meta.type] ?? meta.type
              : '';
            return {
              ...t,
              _metadataTypeLabel: typeLabel,
              _recipientLabel: this.recipientLabel(t),
            };
          });
          this.txs.set(rows);
          this.totalItems.set(res.totalItems ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.txs.set([]);
          this.totalItems.set(0);
          this.totalPages.set(0);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }

  private recipientLabel(t: Transaction): string {
    const name = [t.recipientFirstName, t.recipientLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name || t.recipientNumber || t.recipientEmail || '—';
  }
}
